/**
 * Centrix TypeScript Types
 *
 * Shared type definitions used across all MFEs.
 */

// ============================================================================
// User Types
// ============================================================================

export interface User {
  id: number;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

// ============================================================================
// Authentication Types
// ============================================================================

export interface AuthUserRole {
  enterprise_uuid: string;
  enterprise_name: string;
  role_name: string;
  role_codename: string;
  role_level: number;
}

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_superuser: boolean;
  roles: AuthUserRole[];
  active_enterprise_uuid?: string | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: AuthUser;
  message: string;
}

/**
 * Returned by POST /auth/login/ when the password is correct but the account
 * has MFA enrolled. No auth cookies are set yet — the client must complete the
 * challenge by POSTing `mfa_state_token` + a code to /auth/mfa/verify/.
 */
export interface MfaRequiredResponse {
  mfa_required: true;
  mfa_state_token: string;
  expires_in_seconds: number;
  message: string;
}

/** POST /auth/login/ resolves to either a full login or an MFA challenge. */
export type LoginResult = LoginResponse | MfaRequiredResponse;

/** Discriminates the MFA-challenge branch of {@link LoginResult}. */
export function isMfaRequiredResponse(
  result: LoginResult | undefined | null
): result is MfaRequiredResponse {
  return Boolean(result && 'mfa_required' in result && result.mfa_required);
}

/** Input for POST /auth/mfa/verify/ (completes login after the challenge). */
export interface MfaVerifyRequest {
  mfa_state_token: string;
  /** 6-digit TOTP from an authenticator app, or an XXXXXXXX-XXXXXXXX backup code. */
  code: string;
  /** Register this device as trusted to skip the challenge on future logins. */
  trust_device?: boolean;
  /** Human-readable label stored against the trusted device. */
  device_name?: string;
}

export interface CSRFResponse {
  csrfToken: string;
}

export interface CentrixUserProfile {
  uln?: string;
  secondaryEmail?: string;
  phoneNumber?: string;
  jobTitle?: string;
  department?: string;
  notificationPreferences: Record<string, boolean>;
}

// ============================================================================
// Enterprise Types
// ============================================================================

export interface Enterprise {
  uuid: string;
  name: string;
  slug: string;
  contactEmail?: string;
}

export interface CompanyExtension {
  enterpriseUuid: string;
  settings: Record<string, unknown>;
  primaryColor?: string;
  secondaryColor?: string;
  creditBalance: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Team Types
// ============================================================================

export interface Team {
  uuid: string;
  name: string;
  description?: string;
  managerId?: number;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMembership {
  id: number;
  userId: number;
  username: string;
  email: string;
  joinedAt: string;
}

// ============================================================================
// Subscription Types
// ============================================================================

export interface SubscriptionPlan {
  uuid: string;
  name: string;
  description?: string;
  creditsPerPeriod: number;
  billingPeriod: 'monthly' | 'annual';
  priceCents: number;
  isActive: boolean;
}

export interface CompanySubscription {
  uuid: string;
  planName: string;
  creditsPerPeriod: number;
  status: 'active' | 'past_due' | 'canceled' | 'trialing';
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEnd?: string;
  createdAt: string;
}

export interface CreditTransaction {
  uuid: string;
  amount: number;
  reason: string;
  description?: string;
  createdByUsername?: string;
  createdAt: string;
}

export interface CreditBalance {
  balance: number;
  pendingAllocations: number;
  available: number;
}

// ============================================================================
// Pathway Types
// ============================================================================

export interface Pathway {
  uuid: string;
  name: string;
  description?: string;
  isSequential: boolean;
  isPublished: boolean;
  thumbnailUrl?: string;
  courseCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PathwayCourse {
  id: number;
  courseId: string;
  position: number;
  deadlineDays?: number;
}

export interface PathwayEnrollment {
  uuid: string;
  pathwayName: string;
  status: 'enrolled' | 'in_progress' | 'completed' | 'expired';
  currentPosition: number;
  enrolledAt: string;
  completedAt?: string;
}

// ============================================================================
// Allocation Types
// ============================================================================

export interface CourseAllocation {
  uuid: string;
  username: string;
  userEmail: string;
  courseId: string;
  status: 'allocated' | 'in_progress' | 'completed' | 'expired' | 'refunded';
  teamName?: string;
  pathwayName?: string;
  deadline?: string;
  allocatedByUsername?: string;
  allocatedAt: string;
  completedAt?: string;
}

// ============================================================================
// Announcement Types
// ============================================================================

export interface Announcement {
  uuid: string;
  title: string;
  content: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  targetAll: boolean;
  publishAt: string;
  expiresAt?: string;
  isPublished: boolean;
  isPinned: boolean;
  isActive: boolean;
  isRead: boolean;
  createdByUsername?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Progress Types
// ============================================================================

export interface LearnerProgress {
  uuid: string;
  username: string;
  courseId: string;
  completionPercentage: number;
  gradePercentage?: number;
  totalTimeSpentSeconds: number;
  lastActivityAt?: string;
  unitsCompleted: number;
  unitsTotal: number;
  isComplete: boolean;
  startedAt?: string;
  completedAt?: string;
}

export interface ProgressSummary {
  totalLearners: number;
  completedCount: number;
  inProgressCount: number;
  notStartedCount: number;
  averageCompletion: number;
  averageGrade?: number;
}

// ============================================================================
// Report Types
// ============================================================================

export interface ReportDefinition {
  uuid: string;
  name: string;
  description?: string;
  reportType: string;
  format: 'json' | 'csv' | 'xlsx' | 'pdf';
  parameters: Record<string, unknown>;
  isScheduled: boolean;
  scheduleCron?: string;
  createdByUsername?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReportRun {
  uuid: string;
  definitionName: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  fileUrl?: string;
  fileSizeBytes?: number;
  rowCount?: number;
  createdAt: string;
}

// ============================================================================
// RBAC Types
// ============================================================================

export interface Role {
  uuid: string;
  name: string;
  description?: string;
  level: number;
  permissions: string[];
  isSystemRole: boolean;
}

export interface UserRole {
  id: number;
  username: string;
  roleName: string;
  enterpriseUuid: string;
  teamUuid?: string;
  assignedAt: string;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface ApiResponse<T> {
  status: 'success' | 'error';
  data: T;
  meta?: {
    page?: number;
    pageSize?: number;
    total?: number;
  };
}

export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next?: string;
  previous?: string;
}

export * from './mfe-url';
export * from './roles';
export * from './sectors';
export * from './course';
export * from './courses';
